import { supabaseClient } from "@/lib/supabase";
import { queryOptions, useQuery } from "@tanstack/react-query";

export interface ValidationDetailT {
    validation_id: string;
    is_valid: boolean | null;
    validated_at: string | null;
    reason: string | null;
    on_behalf_of_name: string | null;
    on_behalf_of_organisation: string | null;
    validated_by_name: string | null;
    validated_by_email: string | null;
}

const detailSelect =
    "validation_id, is_valid, validated_at, reason, on_behalf_of_name, on_behalf_of_organisation, profiles(first_name, last_name, email)";

/** Fetched lazily (e.g. on popover open) - not needed for every row just to render a badge. */
export async function selectValidationDetail(validationId: string): Promise<ValidationDetailT | null> {
    const { data, error } = await supabaseClient
        .from("validations")
        .select(detailSelect)
        .eq("validation_id", validationId)
        .maybeSingle();
    if (error) throw error;
    if (!data) return null;

    const name = [data.profiles?.first_name, data.profiles?.last_name].filter(Boolean).join(" ").trim();
    return {
        validation_id: data.validation_id,
        is_valid: data.is_valid,
        validated_at: data.validated_at,
        reason: data.reason,
        on_behalf_of_name: data.on_behalf_of_name,
        on_behalf_of_organisation: data.on_behalf_of_organisation,
        validated_by_name: name || null,
        validated_by_email: data.profiles?.email ?? null,
    };
}

export const validationDetailQueryOptions = (validationId: string | null) =>
    queryOptions({
        queryKey: ["validations", "signoff-detail", validationId],
        queryFn: () => selectValidationDetail(validationId as string),
        enabled: Boolean(validationId),
    });

export function useValidationDetailQuery(validationId: string | null, enabled: boolean) {
    return useQuery({ ...validationDetailQueryOptions(validationId), enabled: Boolean(validationId) && enabled });
}
